import { client } from "api/client";
import { isUnauthorized } from "api/apolloErrorUtils";
import { LOGOUT } from "api/query";
import { queryClient } from "api/queryClient";
import { readUser } from "api/user";
import {
  explorerQuerySessionKeys,
  resetExplorerState,
} from "pages/project/service/explorer/sessionState";
import { authStore } from "stores/authStore";
import { APP_STATE, commonStore } from "stores/commonStore";
import { notificationStore } from "stores/notificationStore";
import { serviceStore } from "stores/serviceStore";
import { userStore } from "stores/userStore";
import {
  COLOR_MODE_STORAGE_KEY,
  COMMAND_PALETTE_RECENTS_STORAGE_KEY,
  REDIRECT_URL,
  THEME_PREFERENCE_STORAGE_KEY,
} from "utils/localStorage";
import { closeSqlWidgetSessions } from "utils/sqlWidgetSessions";

// Logout clears localStorage except for these device preferences. Entries that
// belong to a user must be keyed by user id (like the palette recents), so the
// next person to sign in on this browser can't see them.
const LOCAL_STORAGE_KEPT_ON_LOGOUT = [
  COLOR_MODE_STORAGE_KEY,
  THEME_PREFERENCE_STORAGE_KEY,
  COMMAND_PALETTE_RECENTS_STORAGE_KEY,
];

// No component reads this. It only blocks a second logout.
let isLoggingOut = false;

/**
 * Ends the session and clears the local state of the user. The router and the
 * errorLink call it too, so it is a module function, not a hook.
 *
 * @param redirectUrl stored for post-login restore.
 * @param destination path to land on. Defaults to the logged-out home.
 * @param sessionEnded true when the server answered 401: the session ended
 * already. Then a failed `LOGOUT` does not stop the logout.
 */
export const logout = async ({
  redirectUrl,
  destination = "/",
  sessionEnded = false,
}: {
  redirectUrl?: string;
  destination?: string;
  sessionEnded?: boolean;
} = {}) => {
  // The errorLink calls this for each 401. Run one logout at a time.
  if (isLoggingOut) return;
  try {
    // Without a signed-in user, there is no session to end. The errorLink
    // calls this for each 401, also when no user is signed in.
    if (readUser(client)) {
      isLoggingOut = true;
      authStore.setProgress(true);

      // Close database sessions while authentication is valid, but wait
      // no more than 500 ms before logout.
      try {
        await Promise.race([
          closeSqlWidgetSessions(explorerQuerySessionKeys()),
          new Promise<void>((resolve) => setTimeout(resolve, 500)),
        ]);
      } catch {
        // Session cleanup errors must not prevent logout.
      }
      try {
        resetExplorerState();
      } catch {
        // Explorer state cleanup errors must not prevent logout.
      }

      // A 401 from `LOGOUT` means that the session ended already. Then
      // clear the local state, as for a successful logout. After another
      // error, the session can still be active: keep it, so that the user
      // can try again. When the errorLink got a 401 (`sessionEnded`), the
      // session ended, so clear the local state after any error.
      try {
        const { errors } = await client.query({
          query: LOGOUT,
          fetchPolicy: "no-cache",
        });
        if (errors?.some((error) => !isUnauthorized(error))) {
          throw errors;
        }
      } catch (error) {
        if (!sessionEnded) throw error;
      }

      // The logout query ends the session on the server. Then clear the
      // local state, and load the page again.
      authStore.resetError();
      const keptEntries = LOCAL_STORAGE_KEPT_ON_LOGOUT.flatMap((key) => {
        const value = localStorage.getItem(key);
        return value === null ? [] : [[key, value] as const];
      });

      localStorage.clear();

      keptEntries.forEach(([key, value]) => localStorage.setItem(key, value));

      sessionStorage.clear();
      // The router gates wait for READY. Reset it, so that the next login
      // waits for its own store setup.
      commonStore.setAppState(APP_STATE.INIT);
      userStore.clearStore();
      serviceStore.resetService();
      serviceStore.clearStore();

      await client.clearStore();
      queryClient.clear();

      if (redirectUrl) {
        sessionStorage.setItem(REDIRECT_URL, redirectUrl);
      }

      // Load the page again, not only the route. Then no state of the
      // session that ended stays in memory, and `AuthProvider` decides
      // sign-in from a new `GET_USER`. If `GET_USER` fails after the
      // logout, the app must not show the cached user.
      window.location.assign(destination);
    }
  } catch {
    notificationStore.showErrorToaster("Error logging out, please try again.");
  } finally {
    isLoggingOut = false;
    authStore.setProgress(false);
  }
};
